import { apiFetch, expectJsonBody } from "../api";
import type { BridgeSuitePreview, BridgeSuites, ColumnActivity } from "@/features/bridge-setup/bridge-types";
// Imported rather than re-declared. A second copy of the five step ids is a thing that drifts,
// and this one would drift against the union the wizard actually navigates by. Same reasoning
// for `ACTIVITY_WINDOW_DAYS`, pulled in for `columnActivityQueryKey` below.
import { ACTIVITY_WINDOW_DAYS, type BridgeStepId } from "@/features/bridge-setup/bridge-setup-config";

/** The three PIMS the bridge ships modules for. The wizard only offers Cornerstone. */
export type BridgeIntegrationId = "cornerstone" | "avimark" | "impromed";

/**
 * The bridge's last self-reported health row, as the server returns it.
 *
 * All three of `status`, `db_reachable` and `pubsub_reachable` are nullable, and that is not
 * defensiveness — it's the shape of the wire. POST /bridge/health takes each of them as optional
 * (`bridgeRoutes.ts:573`), stores what it got with `?? null` (`:1301`), and GET /bridge/status
 * returns the row as stored (`:1528`). The bridge exercises this on purpose: its startup
 * deactivation-gate POST (`happy-bridge/bridge/src/main.rs:320-330`) sends `status: healthy` with
 * `db_reachable: None`, so a fresh, correct install produces a row with a null in it.
 *
 * Consequence for readers: `!health.db_reachable` and `!health.pubsub_reachable` are wrong. Both
 * treat "the bridge didn't say" as "the bridge said no", which renders a working install as a
 * failure. Compare `=== false` — that is what the null case needs, not a tidy-up waiting to
 * happen.
 */
export interface BridgeHealth {
    status: "healthy" | "degraded" | "error" | null;
    db_reachable: boolean | null;
    pubsub_reachable: boolean | null;
    queue_depth: number;
    reported_at: string;
}

export interface BridgeStatus {
    installed: boolean;
    is_active?: boolean;
    bridge_version?: string;
    module_type?: string;
    last_seen_at?: string;
    /**
     * The Windows box the bridge runs on. Reported at register time and surfaced only for the
     * wizard's support handoff — it identifies WHICH machine, not what is installed on it.
     */
    machine_hostname?: string | null;
    machine_os_version?: string | null;
    /**
     * EN-990. Which Cornerstone machine role the bridge detected at install time:
     * `server` | `workstation` | `no_cornerstone` | `inconclusive`, or null for
     * every install predating the reporting build. Only `workstation` is acted on.
     */
    cornerstone_machine_role?: "server" | "workstation" | "no_cornerstone" | "inconclusive" | null;
    /**
     * EN-972. Cornerstone's own installed-program version, as detected by the bridge's
     * Windows-registry probe (e.g. "9.1.2026.03") — not the bridge's own crate version, which is
     * `bridge_version` above. Null for every install predating the reporting build.
     */
    cornerstone_version?: string | null;
    /** EN-992. Absolute path to Cornerstone's document store on this machine. */
    cornerstone_directory?: string | null;
    /**
     * The clinic's saved ezyVet address, or `null`/absent if never set. Comes from
     * `bridge_installations.db_host` once an ezyVet install exists (null for any OTHER installed
     * module_type — that column is just whatever PIMS host that module connects to, never an
     * ezyVet address), falling back to the staged `account_settings.ezyvet_base_url` saved before
     * any install exists at all (PR review, 2026-10-06).
     */
    ezyvet_base_url?: string | null;
    latest_health?: BridgeHealth | null;
    /** Where this clinic left off in the wizard; `null` for an account that never started. */
    setup_step?: BridgeStepId | null;
    /**
     * EN-1302. `pending`: the happybridge integration exists but stays inactive until the bridge
     * passes the cutover gate. Missing from an older API. The server also sends null when its
     * settings read fails, so callers treat null as unknown (BridgeSetupFlow keeps the last known
     * state).
     */
    cutover_state?: "pending" | "done" | null;
}

export interface BridgeDownloadUrl {
    url: string;
    expires_at: string;
    version: string;
}

/**
 * Why setup failed, from the server's `code`. The wizard branches on this: `activation_failed`
 * gets a retry button, everything else is a support handoff, because retrying a missing
 * integrations row or an unsupported PIMS will never succeed.
 *
 * `malformed_response` is never sent by the server — it's the client's own sentinel for "the
 * error body didn't parse as JSON at all," which is a different situation from `activation_failed`
 * (a real, JSON-shaped response that simply omitted `code`). See `setupBridgeIntegration`: the two
 * used to collapse into the same fallback, which handed a "Try again" button to a failure we
 * couldn't diagnose and had no reason to believe a retry would fix.
 */
export type BridgeSetupFailureCode =
    | "invalid_integration"
    | "invalid_module"
    | "not_configured"
    | "activation_failed"
    | "malformed_response"
    | "workstation_machine";

export class BridgeSetupError extends Error {
    readonly code: BridgeSetupFailureCode;
    /**
     * Retrying only helps for a transient activation failure.
     *
     * An OWN property set in the constructor, not a prototype getter, and read through
     * `isBridgeSetupError` rather than `instanceof` — both for the same reason. When
     * `class X extends Error` is downlevelled (ts-jest's ES5 emit does this today), the prototype
     * chain is lost: `instanceof` returns false and a prototype getter reads `undefined` on a real
     * instance. `BridgeSetupFlow` then rewraps the error as `activation_failed`, the one code the
     * UI treats as retryable, so a terminal failure like `not_configured` hands a clinic a "Try
     * again" button for something that can never succeed.
     *
     * Production is not downlevelled today (no `@vitejs/plugin-legacy`, no browserslist, no
     * explicit target, so Vite emits native classes) — but this codebase does bend for older
     * Safari elsewhere (`supabaseClient.ts` hand-rolls `AbortController` + `setTimeout` because
     * `AbortSignal.timeout` is missing there), so the target is not guaranteed to stay where it is.
     * The fragility is not hypothetical either: it already bites under ts-jest, and the comment in
     * `BridgeSetupFlow.spec.tsx` records an earlier test that passed just as happily when handed a
     * RETRYABLE error because of it. An own property and a name check survive either target.
     */
    readonly isRetryable: boolean;
    constructor(message: string, code: BridgeSetupFailureCode) {
        super(message);
        this.name = "BridgeSetupError";
        this.code = code;
        this.isRetryable = code === "activation_failed";
    }
}

/**
 * Whether an unknown caught value is one of ours, without depending on the prototype chain.
 *
 * `instanceof` is the obvious spelling and the wrong one here — see `isRetryable` above. `name` is
 * assigned as an own property in the constructor, so it survives downlevelling; `code` is checked
 * too so a plain `Error` that merely happens to be renamed cannot impersonate one.
 */
export const isBridgeSetupError = (error: unknown): error is BridgeSetupError =>
    error instanceof Error && error.name === "BridgeSetupError" && typeof (error as BridgeSetupError).code === "string";

/**
 * The React Query key for `GET bridge/status`, shared by every caller.
 *
 * Several components read this endpoint with different freshness needs — the settings status
 * badge, the upgrade offer beside it (and app-wide), the Integrations page's own gate for the
 * bridge block, and the wizard's entry gate — so they hold separate `useQuery` calls rather than
 * one hook. They must still land on ONE cache entry: several of those render together on
 * Settings → Integrations, and two entries there would mean two requests for the same answer.
 *
 * That agreement used to live in a comment. React Query silently treats a mismatched key as a
 * different query, so drift here has no error and no failing test — just a second request and
 * two components disagreeing about whether the bridge is installed. Exported so the agreement
 * is enforced by the compiler instead.
 *
 * **Every observer of this key must also pass `fetchBridgeStatus` as its `queryFn`**, and that
 * half is NOT compiler-enforced. React Query keeps one `queryFn` per cache entry — whichever
 * observer mounted last — so a second, inline `apiFetch` behind this key makes behaviour depend
 * on mount order. It is not a cosmetic difference: `fetchBridgeStatus` throws on a failed status
 * check, and callers such as `useBridgeUpgradeOffer` depend on a failure being distinguishable
 * from `installed: false`.
 *
 * Note that per-observer options (staleTime, refetchInterval, enabled) stay with each caller and
 * do not conflict; it is the key and the query function that have to match.
 */
export const bridgeStatusQueryKey = (accountId: string | undefined) => ["bridge-status", accountId] as const;

/**
 * Installation state and latest health.
 *
 * Errors propagate — a failed status check is not `installed: false`. Treating it as such
 * would tell a clinic with a working bridge to install it again.
 */
export const fetchBridgeStatus = async (): Promise<BridgeStatus> =>
    expectJsonBody(await apiFetch<BridgeStatus>("bridge/status", { method: "GET" }), "GET bridge/status");

/**
 * The React Query key for `GET bridge/column-activity`, shared by every caller — same reasoning
 * as `bridgeStatusQueryKey` above.
 *
 * `ACTIVITY_WINDOW_DAYS` is part of the key, not just an argument to the query function: it is the
 * window `fetchColumnActivity` is actually called with (see `useColumnActivity`), and baking it in
 * here is what lets `BridgeSetupFlow`'s post-import invalidation match this hook's own key by
 * construction instead of by a second, hand-copied literal that can drift from it.
 */
export const columnActivityQueryKey = (accountId: string | undefined) =>
    ["bridge-column-activity", accountId, ACTIVITY_WINDOW_DAYS] as const;

/**
 * Staff and rooms ranked by recent activity, plus the busiest day in the window.
 *
 * Throws on an empty body rather than returning empty arrays. The wizard reads an empty
 * schedule as an integration fault and routes the clinic to support, so a dropped response
 * must not be able to masquerade as one.
 */
export const fetchColumnActivity = async (days = 60): Promise<ColumnActivity> =>
    expectJsonBody(
        await apiFetch<ColumnActivity>(`bridge/column-activity?days=${days}`, { method: "GET" }),
        "GET bridge/column-activity",
    );

/**
 * The React Query key for `POST bridge/readiness-probe`. Exported for the same reason
 * `bridgeStatusQueryKey` is: a mismatched key is not an error in React Query, just a second
 * request nobody asked for.
 */
export const bridgeReadinessProbeQueryKey = (accountId: string | undefined) =>
    ["bridge-readiness-probe", accountId] as const;

/** What POST bridge/readiness-probe answers. `ready` is the whole payload. */
export interface BridgeReadinessProbe {
    ready: boolean;
}

/**
 * Asks the server to prove the clinic's command channel with a live round trip (EN-969).
 *
 * The 15s client timeout is deliberately longer than the server's own 8s Pub/Sub wait, so a
 * server that is about to answer `{ready: false}` gets to say so rather than being cut off -
 * the difference is only observable in logs, but a request the client abandoned looks like an
 * outage and a request that answered false does not.
 *
 * 404 (no `bridge_installations` row yet) and 401 (account cannot be resolved) are documented,
 * expected outcomes of this endpoint, not faults, and are excluded from Sentry. 404 is the
 * routine one: `useBridgeReadinessProbe` is enabled from the moment the installer is
 * downloaded, so it polls every 10s through the whole window before the bridge first
 * registers, and every poll in that window 404s. The ordinary "probe missed" case is a 200
 * `{ready: false}` returned after the server's own Pub/Sub wait, which never reaches this catch
 * at all. A genuine connectivity failure still reports to Sentry and still shows the toast -
 * deliberately, because the clinic's network really is down and every other call on this
 * screen is saying so too.
 */
export const probeBridgeReadiness = async (): Promise<BridgeReadinessProbe> =>
    expectJsonBody(
        await apiFetch<BridgeReadinessProbe>("bridge/readiness-probe", {
            method: "POST",
            timeout: 15_000,
            skipSentryOn404: true,
            skipSentryOnStatuses: [401],
        }),
        "POST bridge/readiness-probe",
    );

/**
 * Records where the clinic is in the wizard, so abandoning mid-flow is recoverable.
 *
 * Throws like every other helper here. Callers must not block navigation on it: losing the
 * marker costs a resume, not correctness, and a failed POST is not a reason to trap someone on
 * the step they just finished.
 */
export const saveBridgeSetupStep = async (step: BridgeStepId): Promise<void> => {
    await apiFetch("bridge/setup-step", { method: "POST", json: { step }, timeout: 10_000 });
};

/**
 * The React Query key for `GET bridge/suites`, shared by every caller — same reasoning as
 * `bridgeStatusQueryKey` above. A mismatched key here doesn't error, it just silently gives the
 * suite step and any other observer two different answers about which suite is selected.
 *
 * `suiteId` is part of the key, not just an argument to the query function: `GET /bridge/suites`
 * computes `rooms` / `in_selected_suite` for exactly ONE suite, so a cached answer for suite 12
 * must never be handed back for suite 24. Normalized to `null` for "no suite chosen yet" so that
 * case has a single, cacheable key rather than colliding with `undefined`.
 */
export const bridgeSuitesQueryKey = (accountId: string | undefined, suiteId?: number | null) =>
    ["bridge-suites", accountId, suiteId ?? null] as const;

/**
 * Every cached suites answer for one account, whichever suite it was keyed to.
 *
 * Exists so a caller that genuinely wants prefix semantics — the suite step's retry, which must
 * also catch a read fired right after a suite change but before that refetch settled — can say so
 * with the shared builder instead of a bare `["bridge-suites"]` literal. A literal here and the
 * hook's own key drifting apart is the exact failure `bridgeSuitesQueryKey` was added to prevent.
 */
export const bridgeSuitesQueryPrefix = (accountId: string | undefined) => ["bridge-suites", accountId] as const;

/**
 * Suites, rooms, and the activity-based recommendation for the Cornerstone "appointment suite"
 * step.
 *
 * `suiteId` asks the server to compute the coverage warning (`rooms` / `in_selected_suite`)
 * against that suite rather than its own default pick. Omitted on the first load, when nothing
 * has been chosen yet.
 *
 * Throws on an empty body rather than returning an empty suite list, for the same reason as
 * `fetchColumnActivity`: the wizard reads an empty result as a clinic with no suites, and a
 * dropped response must not be able to masquerade as one.
 */
export const fetchBridgeSuites = async (suiteId?: number | null): Promise<BridgeSuites> =>
    expectJsonBody(
        await apiFetch<BridgeSuites>(suiteId != null ? `bridge/suites?suite_id=${suiteId}` : "bridge/suites", {
            method: "GET",
        }),
        "GET bridge/suites",
    );

/**
 * The React Query key for `GET bridge/suite-preview`. Not keyed by suite id: the preview is the
 * whole day across every chair, and which columns are IN the chosen suite is decided client-side
 * from `BridgeSuites.rooms`. Keying it per suite would refetch the identical payload on every
 * dropdown change.
 */
export const suitePreviewQueryKey = (accountId: string | undefined) => ["bridge-suite-preview", accountId] as const;

/**
 * Explicit 200s timeout, well above `apiFetch`'s 30s default.
 *
 * The server can legitimately spend far longer than 30s here. Each bridge read allows the ODBC
 * SELECT 90s, and a quiet install (an early morning or a Sunday, the case this endpoint's
 * fallback exists for) makes two of them back to back. Giving up at 30s would abort a request
 * that was going to succeed, and because a failed preview is indistinguishable from an empty
 * board without reading `isError`, the clinic would simply be told nothing is scheduled. Same
 * convention as every other bridge call whose server side can exceed the default.
 */
const SUITE_PREVIEW_TIMEOUT_MS = 200_000;

export const fetchSuitePreview = async (): Promise<BridgeSuitePreview> =>
    expectJsonBody(
        await apiFetch<BridgeSuitePreview>("bridge/suite-preview", {
            method: "GET",
            timeout: SUITE_PREVIEW_TIMEOUT_MS,
        }),
        "GET bridge/suite-preview",
    );

/**
 * Persists a clinic's dragged column order (EN-1199 Task 8). `chairNums` is the FULL ordered
 * list the grid currently shows, not a delta: the server redistributes these rooms across the
 * `sort_index` slots they already occupy, in this order. It deliberately does NOT renumber the
 * account's other rooms, because the RPC behind it stamps `sort_index_source` on every row it
 * writes and that would freeze untouched rooms against the nightly Cornerstone resync.
 *
 * Can 409 when a PIMS sync currently holds `bridge:sync-providers:{accountId}`, a real,
 * retryable state, not a generic failure. `apiFetch` throws ky's `HTTPError` on a non-2xx
 * response, so a caller that wants to tell the two apart reads `(error as
 * HTTPError)?.response?.status`, the same duck-typing `setupBridgeIntegration` below uses.
 */
export const saveRoomOrder = async (chairNums: string[]): Promise<{ updated: number }> =>
    expectJsonBody(
        await apiFetch<{ updated: number }>("bridge/room-order", {
            method: "PATCH",
            json: { chair_nums: chairNums },
        }),
        "PATCH bridge/room-order",
    );

export type OldDoctorClassification = "suggested" | "ambiguous" | "unmappable";

export type PendingDoctorAction =
    | { action: "map"; bridgeDoctorId: string }
    | { action: "archive" }
    | { action: "fold"; intoDoctorId: string };

export interface OldDoctorMapping {
    doctorId: string;
    name: string;
    pmsEmployeeId: string;
    pmsExportId: string;
    classification: OldDoctorClassification;
    candidates: string[];
    suggestedBridgeDoctorId: string | null;
    syncExcluded: boolean;
    appearsOnAppointments: boolean;
    /** Appointments in the recent window, or null when activity is unavailable. */
    recentVisits: number | null;
    /** A decision recorded while the cutover is pending; applied when the switch completes. */
    pendingAction: PendingDoctorAction | null;
    /** Holds the cutover until mapped, archived, or a decision is recorded. */
    blocking: boolean;
}

export interface BridgeDoctorOption {
    doctorId: string;
    name: string;
    pmsEmployeeId: string;
    /** The ezyVet clinic the provider belongs to; null for Cornerstone. */
    detail: string | null;
    candidateFor: string[];
    /** Already merged with its bridge row. Can be offered as a candidate but is never suggested, and can only be folded into. */
    merged: boolean;
}

export interface DoctorMapping {
    /** The clinic the server resolved the request to; callers check it against the one they asked for. */
    accountId: string;
    /** Which old-row rules the server used; copy names the PIMS. */
    mode: "bitwerx" | "ezyvet";
    pimsName: string;
    oldDoctors: OldDoctorMapping[];
    bridgeDoctors: BridgeDoctorOption[];
    activityAvailable: boolean;
}

export type DoctorMappingAction =
    | { doctorId: string; action: "map"; bridgeDoctorId: string }
    | { doctorId: string; action: "archive" }
    | { doctorId: string; action: "fold"; intoDoctorId: string };

/**
 * The React Query key for `GET bridge/doctor-mapping`, shared by every caller.
 */
export const doctorMappingQueryKey = (accountId: string | undefined) => ["bridge-doctor-mapping", accountId] as const;

/** EN-997. Bitwerx-era doctors beside the bridge providers they could be. */
export const fetchDoctorMapping = async (accountId: string): Promise<DoctorMapping> => {
    const mapping = expectJsonBody(
        await apiFetch<DoctorMapping>("bridge/doctor-mapping", { method: "GET" }),
        "GET bridge/doctor-mapping",
    );
    // apiFetch sends whichever clinic is selected when the request goes out; a switch since the
    // caller keyed its query must not cache another clinic's mapping under this key.
    if (mapping.accountId !== accountId) throw new Error("doctor mapping response was for a different clinic");
    return mapping;
};

/**
 * Can 409 while a staff/room sync holds `bridge:sync-providers:{accountId}` (retryable), and 400
 * with a message naming the conflict when two doctors claim one provider. The server also 409s
 * `account_changed` when `accountId` is not the clinic the request resolved to.
 */
export const saveDoctorMapping = async (accountId: string, actions: DoctorMappingAction[]): Promise<DoctorMapping> =>
    expectJsonBody(
        // 400 (conflicting choices) and 409 (a sync holds the lock) are answers the caller shows
        // the clinic, not faults: keep them out of Sentry and the generic network-error toast.
        await apiFetch<DoctorMapping>("bridge/doctor-mapping", {
            method: "POST",
            json: { accountId, actions },
            skipSentryOnStatuses: [400, 409],
        }),
        "POST bridge/doctor-mapping",
    );

export interface SaveBridgeSuiteParams {
    suiteId: number;
    /**
     * How this suite was chosen. `BridgeSuites["recommendation_source"]` describes how the SERVER
     * arrived at a recommendation and does not include `"manual"` — but `"manual"` is exactly what
     * `SuiteChoiceStep` sends when a clinic picks a suite other than the one recommended, and it's
     * one of the values `account_settings.cornerstone_appt_suite_source`'s column comment
     * documents. Widened here rather than cast away at the call site: this column is the only
     * record of how a clinic ended up on a suite, and there's no server-side CHECK constraint to
     * catch a type that can't express its real values.
     */
    source: BridgeSuites["recommendation_source"] | "manual";
    /** Chair numbers the vet added on top of the suite's own rooms, so the server can mark them manual. */
    addedChairNums: string[];
    /** Chairs IN the chosen suite that the clinic unchecked, so the server can mark them manual. */
    removedChairNums: string[];
}

/**
 * `POST /bridge/suite`'s response.
 *
 * `manual_mark_failed` is the field the wizard has to react to: the suite itself applied, but
 * writing `sync_excluded_source = 'manual'` for the rooms the clinic added OUTSIDE the suite
 * failed. That tag is the only thing stopping the unattended nightly re-apply from re-excluding
 * those rooms — without it landing, the rooms the clinic just deliberately added back disappear
 * from their calendar the next day, silently.
 *
 * `applied: false` splits into two causes the wizard must NOT treat the same:
 * - `suite_is_empty: false` — the room read failed. Transient; the next nightly re-apply
 *   converges on its own without the clinic doing anything.
 * - `suite_is_empty: true` — the read succeeded and the chosen suite genuinely has zero enabled
 *   rooms (e.g. every `APPT_SUITES_DETAIL` row has `display_room = 0`). This does NOT converge —
 *   tonight's re-apply hits the identical empty list, and so does every night after. The clinic
 *   picked a suite that will filter their entire calendar forever unless told to pick a
 *   different one.
 */
export interface SaveBridgeSuiteResult {
    applied: boolean;
    manual_mark_failed: boolean;
    /**
     * The room read SUCCEEDED and the chosen suite genuinely has zero enabled rooms — distinct
     * from `applied: false` with this false, which means the read itself failed and the next
     * nightly re-apply converges. This one never converges: every re-apply reads the same empty
     * list, so the clinic has to pick a different suite.
     */
    suite_is_empty: boolean;
    /**
     * How many rooms are left showing on the calendar after the apply — the number the Done
     * screen reports as "Rooms being synced to HappyDoc".
     *
     * OPTIONAL, and it must stay optional. The two repos do not deploy together, so a frontend
     * carrying this feature will talk to an API that predates the field. `undefined` means "the
     * server didn't tell us" and must never be coerced to `0`, which the Done screen reads as a
     * clinic that deliberately synced nothing.
     *
     * Only ever sent with `applied: true`. The soft-fail branches never ran the exclusion pass.
     */
    visible?: number;
}

/**
 * `timeout` is load-bearing and must stay ABOVE the server's own worst case, not at `apiFetch`'s
 * 30s default.
 *
 * The server bounds its suite-rooms read at `AbortSignal.timeout(30_000)` (`bridgeRoutes.ts`) and
 * then still has work to do — the exclusion pass and the response. So when a bridge is
 * unresponsive rather than merely erroring, the soft-fail answer takes just over 30s: measured at
 * **30.9s** against a real bridge with its process stopped. At the 30s default the client aborted
 * roughly a second before that response arrived, EVERY time.
 *
 * The consequence was not a slow screen, it was the wrong one. The server was correctly returning
 * `{ applied: false, suite_is_empty: false }` — the shape that sends the wizard to the fallback
 * room picker — and the clinic instead got `handleConfirmSuite`'s catch branch, "Couldn't save
 * that. Try again.", stranded on the suite step. That made the fallback picker unreachable in
 * exactly the scenario it exists for, which is the whole reason the sync step was kept (EN-974).
 *
 * 60s, not a smaller bump: it clears the measured 30.9s with room for the manual-chair stamps and
 * a slow exclusion pass on a large account, and this is the failure path — the happy path returns
 * in well under a second. Waiting is the correct trade against showing a false error. Raising the
 * ceiling here rather than shortening the server's 30s read, which is sized for genuinely slow
 * Cornerstone reads and would start failing legitimate ones.
 */
export const saveBridgeSuite = async ({
    suiteId,
    source,
    addedChairNums,
    removedChairNums,
}: SaveBridgeSuiteParams): Promise<SaveBridgeSuiteResult> =>
    expectJsonBody(
        await apiFetch<SaveBridgeSuiteResult>("bridge/suite", {
            method: "POST",
            json: { suite_id: suiteId, source, added_chair_nums: addedChairNums, removed_chair_nums: removedChairNums },
            timeout: 60_000,
        }),
        "POST bridge/suite",
    );

export const fetchBridgeDownloadUrl = async (integration: BridgeIntegrationId): Promise<BridgeDownloadUrl> =>
    expectJsonBody(
        await apiFetch<BridgeDownloadUrl>(`bridge/download?integration=${integration}`, {
            method: "GET",
            timeout: 30_000,
        }),
        "GET bridge/download",
    );

/**
 * Creates the clinic's happybridge integration. `activated: true` means it is now the live
 * integration (the account had no other one). `activated: false` means another integration is
 * still live and the switch will happen automatically once the bridge is healthy and has read
 * the clinic's staff or rooms (EN-1302). Await it and treat a thrown error as blocking.
 */
export const setupBridgeIntegration = async (integration: BridgeIntegrationId): Promise<{ activated: boolean }> => {
    try {
        const body = expectJsonBody(
            await apiFetch<{ success?: boolean; activated?: boolean }>("bridge/setup-integration", {
                method: "POST",
                json: { integration },
                timeout: 15_000,
            }),
            "POST bridge/setup-integration",
        );
        // An older API always sent activated: true. Default to true so a missing field
        // behaves exactly as before. Only a real boolean is trusted: anything else (a string,
        // null) is treated like a missing field rather than guessed at.
        return { activated: typeof body.activated === "boolean" ? body.activated : true };
    } catch (error: unknown) {
        // A server response (ky's HTTPError, or anything shaped like it) carries a `code` we
        // can surface. Duck-typed on `.response` rather than `instanceof HTTPError`: apiFetch's
        // catch re-throws whatever ky threw without rewrapping, so a real HTTPError always has
        // this shape, and it lets tests construct a plain Error with a `.response` stand-in
        // without needing to fabricate ky's actual class. Anything without a `.response`
        // (network failure, abort) propagates as-is — there's no server verdict to translate.
        const res = (error as { response?: { json: () => Promise<{ error?: string; code?: string }> } })?.response;
        if (!res) throw error;
        let body: { error?: string; code?: string } = {};
        // Distinct from "parsed fine, just no `code` field" (the `{}` case below, which is a real
        // server response and stays on the retryable default) — this is "we don't even know what
        // the server said." Collapsing the two meant a body that failed to parse defaulted to
        // `activation_failed`, the one code `BridgeSetupError.isRetryable` treats as retryable, so
        // a terminal failure whose response happened to be non-JSON showed the clinic a "Try
        // again" button for something retrying can never fix.
        // Distinct from "parsed fine, just no `code` field" (the `{}` case below, which is a real
        // server response and stays on the retryable default) — this is "we don't even know what
        // the server said." Collapsing the two meant a body that failed to parse defaulted to
        // `activation_failed`, the one code `BridgeSetupError.isRetryable` treats as retryable, so
        // a terminal failure whose response happened to be non-JSON showed the clinic a "Try
        // again" button for something retrying can never fix.
        let bodyParsed = true;
        try {
            body = await res.json();
        } catch {
            bodyParsed = false;
        }
        throw new BridgeSetupError(
            body.error ?? "Could not connect to Cornerstone.",
            (body.code as BridgeSetupFailureCode) ?? (bodyParsed ? "activation_failed" : "malformed_response"),
        );
    }
};

/** The React Query key for `GET bridge/ezyvet-clinics`. */
export const ezyvetClinicsQueryKey = (accountId?: string) => ["ezyvet-clinics", accountId] as const;

export interface EzyvetClinicsResponse {
    clinics: { id: string; uid: string; name: string }[];
    selected: string[];
}

/** The ezyVet database's clinics, plus which ones this HappyDoc account is. 409 = no API credentials yet. */
export const fetchEzyvetClinics = async (): Promise<EzyvetClinicsResponse> =>
    expectJsonBody(await apiFetch<EzyvetClinicsResponse>("bridge/ezyvet-clinics"), "GET bridge/ezyvet-clinics");

/** Saves which ezyVet clinics this account is. `[]` = all clinics. */
export const saveEzyvetClinics = async (ids: string[]): Promise<{ selected: string[] }> =>
    expectJsonBody(
        await apiFetch<{ selected: string[] }>("bridge/ezyvet-clinics", {
            method: "PUT",
            json: { clinic_ids: ids },
            timeout: 15_000,
        }),
        "PUT bridge/ezyvet-clinics",
    );
